import Icon from "@imminently/decisively-ui/Icon";
import ButtonGroup from "@imminently/decisively-ui/ButtonGroup";
import * as React from "react"
import { useState } from "react"
import Button, { type ButtonVariant, type ButtonIntent, type ButtonSize } from "@imminently/decisively-ui/Button"
import Select from "@imminently/decisively-ui/Select"
import Switch from "@imminently/decisively-ui/Switch"
import { Label } from "@/ui/primitives/label"

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
  VariantGrid,
  VariantItem,
} from "../components/ComponentPage"

export function ButtonPage() {
  const [variant, setVariant] = useState<ButtonVariant>("contained")
  const [intent, setIntent] = useState<ButtonIntent>("primary")
  const [size, setSize] = useState<ButtonSize>("default")
  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [withIcon, setWithIcon] = useState(false)
  const [icon, setIcon] = useState(false)
  const [tooltip, setTooltip] = useState(false)
  const [allowDisabledTooltips, setAllowDisabledTooltips] = useState(false)

  return (
    <ComponentPage
      title="Button"
      description="Displays a button or a component that looks like a button."
    >
      {/* Interactive Playground */}
      <ComponentSection
        title="Playground"
        description="Experiment with different button configurations"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <Button label={icon ? "Email action" : "Button"}
              variant={variant}
              intent={intent}
              size={size}
              loading={loading}
              disabled={disabled}
              icon={icon}
              tooltip={tooltip ? "This is a tooltip" : undefined}
              allowDisabledTooltips={allowDisabledTooltips}
            >
              {!icon && withIcon && <Icon name="email" />}
              {icon ? <Icon name="email" /> : "Button"}
            </Button>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Variant">
              <Select
                value={variant}
                onValueChange={(v) => setVariant((v ?? "contained") as ButtonVariant)}
                options={[
                  { value: "contained", label: "Contained" },
                  { value: "outline", label: "Outline" },
                  { value: "ghost", label: "Ghost" },
                  { value: "link", label: "Link" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Intent">
              <Select
                value={intent}
                onValueChange={(v) => setIntent((v ?? "primary") as ButtonIntent)}
                options={[
                  { value: "primary", label: "Primary" },
                  { value: "secondary", label: "Secondary" },
                  { value: "destructive", label: "Destructive" },
                  { value: "warning", label: "Warning" },
                  { value: "sidebar", label: "Sidebar" },
                  { value: "dark", label: "Dark" },
                  { value: "light", label: "Light" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Size">
              <Select
                value={size}
                onValueChange={(v) => setSize((v ?? "default") as ButtonSize)}
                options={[
                  { value: "xs", label: "Extra small" },
                  { value: "sm", label: "Small" },
                  { value: "md", label: "Medium" },
                  { value: "default", label: "Default" },
                  { value: "lg", label: "Large" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Loading">
              <Switch checked={loading} onCheckedChange={setLoading} />
            </ControlRow>
            <ControlRow label="Disabled">
              <Switch checked={disabled} onCheckedChange={setDisabled} />
            </ControlRow>
            <ControlRow label="With Icon">
              <Switch checked={withIcon} onCheckedChange={setWithIcon} />
            </ControlRow>
            <ControlRow label="Icon mode">
              <Switch checked={icon} onCheckedChange={setIcon} />
            </ControlRow>
            <ControlRow label="Tooltip">
              <Switch checked={tooltip} onCheckedChange={setTooltip} />
            </ControlRow>
            <ControlRow label="Allow Disabled Tooltip">
              <Switch checked={allowDisabledTooltips} onCheckedChange={setAllowDisabledTooltips} />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* All Variants */}
      <ComponentSection title="Variants" description="All visual style variants">
        <ComponentPreview className="flex-wrap gap-4">
          <Button variant="contained" label="Contained">Contained</Button>
          <Button variant="outline" label="Outline">Outline</Button>
          <Button variant="ghost" label="Ghost">Ghost</Button>
          <Button variant="link" label="Link">Link</Button>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection title="Groups" description="Adjacent buttons can be grouped into a single control.">
        <ComponentPreview className="flex-wrap gap-6">
          <ButtonGroup>
            <Button variant="outline" label="Scenarios">Scenarios</Button>
            <Button icon variant="outline" label="Refresh">
              <Icon name="refresh" />
            </Button>
          </ButtonGroup>

          <ButtonGroup orientation="vertical">
            <Button variant="outline" label="Run">
              <Icon name="play" />
              Run
            </Button>
            <Button variant="outline" label="Stop">
              <Icon name="stop" />
              Stop
            </Button>
            <Button variant="outline" label="Download">
              <Icon name="download" />
              Download
            </Button>
          </ButtonGroup>
        </ComponentPreview>
      </ComponentSection>

      {/* All Intents */}
      <ComponentSection title="Intents" description="Color intentions for different actions">
        <ComponentPreview className="flex-wrap gap-4">
          <Button intent="primary" label="Primary">Primary</Button>
          <Button intent="secondary" label="Secondary">Secondary</Button>
          <Button intent="destructive" label="Destructive">Destructive</Button>
          <Button intent="warning" label="Warning">Warning</Button>
          <Button intent="sidebar" variant="ghost" label="Sidebar">Sidebar</Button>
          <Button intent="dark" label="Dark">Dark</Button>
          <Button intent="light" label="Light">Light</Button>
        </ComponentPreview>
      </ComponentSection>

      {/* Variant × Intent Matrix */}
      <ComponentSection title="Variant × Intent Matrix" description="All combinations of variants and intents">
        <div className="overflow-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-3 text-left text-sm font-medium text-muted-foreground"></th>
                <th className="p-3 text-center text-sm font-medium text-muted-foreground">Primary</th>
                <th className="p-3 text-center text-sm font-medium text-muted-foreground">Secondary</th>
                <th className="p-3 text-center text-sm font-medium text-muted-foreground">Destructive</th>
                <th className="p-3 text-center text-sm font-medium text-muted-foreground">Warning</th>
                <th className="p-3 text-center text-sm font-medium text-muted-foreground">Sidebar</th>
                <th className="p-3 text-center text-sm font-medium text-muted-foreground">Dark</th>
                <th className="p-3 text-center text-sm font-medium text-muted-foreground">Light</th>
              </tr>
            </thead>
            <tbody>
              {(["contained", "outline", "ghost", "link"] as const).map((v) => (
                <tr key={v} className="border-t border-border">
                  <td className="p-3 text-sm font-medium capitalize">{v}</td>
                  {(["primary", "secondary", "destructive", "warning", "sidebar", "dark", "light"] as const).map((i) => (
                    <td key={i} className="p-3 text-center">
                      <Button variant={v} intent={i} size="sm" label="Small">Small</Button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ComponentSection>

      {/* Sizes */}
      <ComponentSection title="Sizes" description="Available button sizes">
        <ComponentPreview className="flex-wrap gap-4 items-center">
          <Button size="xs" label="Extra small">Extra small</Button>
          <Button size="sm" label="Small">Small</Button>
          <Button size="md" label="Medium">Medium</Button>
          <Button size="default" label="Default">Default</Button>
          <Button size="lg" label="Large">Large</Button>
        </ComponentPreview>
      </ComponentSection>

      {/* Icon mode */}
      <ComponentSection title="Icon mode" description="Square icon-only buttons across sizes">
        <ComponentPreview className="flex-wrap gap-4 items-center">
          <Button label="Delete" icon size="xs"><Icon name="delete" /></Button>
          <Button label="Delete" icon size="sm"><Icon name="delete" /></Button>
          <Button label="Delete" icon size="md"><Icon name="delete" /></Button>
          <Button label="Delete" icon size="default"><Icon name="delete" /></Button>
          <Button label="Delete" icon size="lg"><Icon name="delete" /></Button>
        </ComponentPreview>
      </ComponentSection>

      {/* With Icons */}
      <ComponentSection title="With Icons" description="Buttons with leading or trailing icons">
        <ComponentPreview className="flex-wrap gap-4">
          <Button  label="Login with Email">
            <Icon name="email" /> Login with Email
          </Button>
          <Button  variant="outline" label="Continue">
            Continue <Icon name="go-forward" />
          </Button>
          <Button  variant="ghost" intent="destructive" label="Delete">
            <Icon name="delete" /> Delete
          </Button>
        </ComponentPreview>
      </ComponentSection>

      {/* Tooltips */}
      <ComponentSection title="Tooltips" description="Buttons with tooltip and hotkey hints">
        <ComponentPreview className="flex-wrap gap-4">
          <Button tooltip="Save the document" label="Save">Save</Button>
          <Button tooltip="Undo last action" hotkeys={["ctrl", "z"]} variant="outline" label="Undo">Undo</Button>
          <Button label="Delete item" icon tooltip="Delete item" intent="destructive" variant="ghost"><Icon name="delete" /></Button>
          <Button disabled tooltip="You don't have permission" allowDisabledTooltips label="Restricted">Restricted</Button>
        </ComponentPreview>
      </ComponentSection>

      {/* States */}
      <ComponentSection title="States" description="Loading and disabled states">
        <ComponentPreview className="flex-wrap gap-4">
          <Button loading label="Loading">Loading</Button>
          <Button disabled label="Disabled">Disabled</Button>
          <Button variant="outline" loading label="Loading">Loading</Button>
          <Button variant="outline" disabled label="Disabled">Disabled</Button>
        </ComponentPreview>
      </ComponentSection>

    </ComponentPage>
  )
}
